import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  getMatcherConfigWarningDetails,
  MATCHER_CONFIG_WARNING_MESSAGE,
  shouldShowMatcherConfigWarning,
} from './regressionSuiteHealthUi';
import {
  COVERAGE_FILTER_OPTIONS,
  COVERAGE_FILTERS,
  EMPTY_STRUCTURED_REPORT,
  filterCoverageUnits,
  formatMatchScore,
  formatSourceLabel,
  getBestMatchedTestLabel,
  getCoverageFilterCounts,
  getCriticalQualifierSummary,
  getEmptyFilterMessage,
  getMatchStrength,
  getRecommendedActionTone,
  getStatusChipColor,
  getValidationSections,
  STATUS_LABELS,
} from './regressionCoveragePresentation';
import {
  COVERAGE_UNITS_EXPLANATION,
  PARTIAL_MATCH_BANNER_TEXT,
} from './regressionCoverageEvidencePresentation';
import RegressionValidationCoverageSummary from './RegressionValidationCoverageSummary';
import { DECISION_CHIP_MAP } from './regressionPartialReviewPresentation';

const RecommendedActionChip = ({ action }) => {
  if (!action) return null;
  return (
    <Chip
      size="small"
      label={action}
      color={getRecommendedActionTone(action)}
      variant="outlined"
      sx={{ fontWeight: 600 }}
    />
  );
};

const CoverageUnitEvidence = ({ unit }) => {
  const relatedTests = unit.possible_related_tests || [];
  const qualifierSummary = getCriticalQualifierSummary(unit.match_signals || {});
  const matchStrength = getMatchStrength(unit.match_score);

  return (
    <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        Raw match score: {formatMatchScore(unit.match_score)} ({matchStrength.label})
      </Typography>
      {unit.match_reason && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Match reason: {unit.match_reason}
        </Typography>
      )}
      {relatedTests.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="warning.main" display="block" sx={{ fontWeight: 600 }}>
            Possible related tests
          </Typography>
          {relatedTests.map((test) => (
            <Typography key={test.test_id} variant="caption" color="text.secondary" display="block">
              {test.test_title || test.test_id}
              {test.match_score != null ? ` (${formatMatchScore(test.match_score)})` : ''}
            </Typography>
          ))}
        </Box>
      )}
      {qualifierSummary && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
            Critical qualifiers
          </Typography>
          {qualifierSummary.required.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block">
              Required: {qualifierSummary.required.join(', ')}
            </Typography>
          )}
          {qualifierSummary.matched.length > 0 && (
            <Typography variant="caption" color="success.main" display="block">
              Matched: {qualifierSummary.matched.join(', ')}
            </Typography>
          )}
          {qualifierSummary.missing.length > 0 && (
            <Typography variant="caption" color="warning.main" display="block">
              Missing: {qualifierSummary.missing.join(', ')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const CoverageUnitRow = ({ unit }) => {
  const [expanded, setExpanded] = useState(false);
  const status = unit.status || 'missing';
  const matchStrength = getMatchStrength(unit.match_score);
  const bestMatchedTest = getBestMatchedTestLabel(unit);
  const reviewDecision = unit?.partial_review?.decision;
  const reviewChip = DECISION_CHIP_MAP[reviewDecision] || null;
  const strengthTooltip = matchStrength.raw == null
    ? 'No match score available'
    : `Raw match score: ${formatMatchScore(matchStrength.raw)}`;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {unit.unit_name || unit.unit_text || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {unit.module_key || '-'} · {unit.unit_type || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            Source: {formatSourceLabel(unit.source)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {bestMatchedTest ? `Best matched test: ${bestMatchedTest}` : 'Best matched test: —'}
          </Typography>
          {status === 'partial' && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5, fontWeight: 600 }}>
              Does not count as covered — match strength or qualifiers did not meet the covered threshold.
            </Typography>
          )}
          {unit.recommended_action && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Recommended action: {unit.recommended_action}
            </Typography>
          )}
          {unit.match_reason && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              Reason: {unit.match_reason}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75, flexShrink: 0 }}>
          <Chip
            size="small"
            label={STATUS_LABELS[status] || status}
            color={getStatusChipColor(status)}
          />
          {status === 'partial' && reviewChip && (
            <Chip
              size="small"
              label={reviewChip.label}
              color={reviewChip.color}
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          )}
          <Tooltip title={strengthTooltip}>
            <Chip
              size="small"
              variant="outlined"
              label={`Match strength: ${matchStrength.label}`}
              sx={{ color: 'text.secondary', borderColor: 'divider' }}
            />
          </Tooltip>
          <RecommendedActionChip action={unit.recommended_action} />
          <IconButton size="small" onClick={() => setExpanded((prev) => !prev)} aria-label="Toggle evidence details">
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <CoverageUnitEvidence unit={unit} />
      </Collapse>
    </Paper>
  );
};

const GeneratedTestsSection = ({ generatedTests }) => {
  if (!generatedTests.length) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Generated test suggestions
      </Typography>
      <Alert severity="info" sx={{ mb: 1.5 }}>
        Generated suggestions do not count as covered until approved.
      </Alert>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Test</TableCell>
              <TableCell>Source coverage unit</TableCell>
              <TableCell>Source requirement</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {generatedTests.map((item) => (
              <TableRow key={item.test_id}>
                <TableCell>{item.test_title || item.test_id}</TableCell>
                <TableCell>{item.coverage_unit_name || item.coverage_unit_id || '-'}</TableCell>
                <TableCell>{item.source_pkb_entry_title || '-'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={item.status_label || 'Pending approval'}
                    color="warning"
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const RegressionValidationRunDetails = ({ run }) => {
  const [activeFilter, setActiveFilter] = useState(COVERAGE_FILTERS.REVIEW_NEEDED);
  const structuredReport = run?.structured_report;
  const sections = useMemo(
    () => getValidationSections(structuredReport || EMPTY_STRUCTURED_REPORT),
    [structuredReport]
  );
  const filterCounts = useMemo(
    () => getCoverageFilterCounts(structuredReport || EMPTY_STRUCTURED_REPORT),
    [structuredReport]
  );
  const filteredUnits = useMemo(
    () => filterCoverageUnits(structuredReport || EMPTY_STRUCTURED_REPORT, activeFilter),
    [structuredReport, activeFilter]
  );

  const matchingMetadata = run?.report?.matching_metadata || structuredReport?.matching_metadata || null;
  const showMatcherConfigWarning = shouldShowMatcherConfigWarning(matchingMetadata);
  const matcherConfigWarningDetails = getMatcherConfigWarningDetails(matchingMetadata);
  const partialUnitsCount = sections.summary.maybe_units ?? 0;

  const handleFilterChange = (_event, nextFilter) => {
    if (nextFilter) setActiveFilter(nextFilter);
  };

  return (
    <Box>
      {showMatcherConfigWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {MATCHER_CONFIG_WARNING_MESSAGE}
          {matcherConfigWarningDetails && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {matcherConfigWarningDetails}
            </Typography>
          )}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        {COVERAGE_UNITS_EXPLANATION}
      </Alert>

      <RegressionValidationCoverageSummary
        structuredReport={structuredReport || EMPTY_STRUCTURED_REPORT}
        rawReport={run?.report}
      />

      {partialUnitsCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {PARTIAL_MATCH_BANNER_TEXT}
        </Alert>
      )}

      {sections.moduleRollup.length > 0 && (
        <Accordion disableGutters elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Module breakdown</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Module</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Covered</TableCell>
                    <TableCell>Partial</TableCell>
                    <TableCell>Missing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sections.moduleRollup.map((row) => (
                    <TableRow key={row.module_key}>
                      <TableCell>{row.module_key}</TableCell>
                      <TableCell>{row.total ?? 0}</TableCell>
                      <TableCell>{row.covered ?? 0}</TableCell>
                      <TableCell>{row.maybe ?? 0}</TableCell>
                      <TableCell>{row.missing ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Coverage units
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={activeFilter}
          exclusive
          onChange={handleFilterChange}
          aria-label="Coverage unit filter"
        >
          {COVERAGE_FILTER_OPTIONS.map((option) => (
            <ToggleButton key={option.id} value={option.id}>
              {option.label} ({filterCounts[option.id] ?? 0})
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {filteredUnits.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {getEmptyFilterMessage(activeFilter)}
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }}>
          {filteredUnits.map((unit) => (
            <CoverageUnitRow
              key={unit.coverage_unit_id || `${unit.module_key}-${unit.unit_name}-${unit.status}`}
              unit={unit}
            />
          ))}
        </Box>
      )}

      {activeFilter !== COVERAGE_FILTERS.COVERED
        && activeFilter !== COVERAGE_FILTERS.ALL
        && sections.coveredUnits.length > 0 && (
        <Accordion disableGutters elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              Covered units ({sections.coveredUnits.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {sections.coveredUnits.map((unit) => (
              <CoverageUnitRow
                key={unit.coverage_unit_id || `${unit.module_key}-${unit.unit_name}-covered`}
                unit={unit}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      <GeneratedTestsSection generatedTests={sections.generatedTests} />
    </Box>
  );
};

export default RegressionValidationRunDetails;
